import React from 'react';
import { BaseStep, FormField, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import foundersIcon from '../../../../assets/icons/founders.png';
import PillSelector from '../PillSelector';
import { AddButton } from '../AddButton';
import { RemoveButton } from '../RemoveButton';
import { Toggle } from '../Toggle';
import { useProfile } from '../../../../contexts/ProfileContext';
import { useAuth } from '../../../../contexts/AuthContext';
import CountrySelect from '../../../CountrySelect';
import { loadPricingSignupDraft } from '../../../../utils/pricingSignupDraft';

const founderCountOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3+', label: '3+' },
];

const teamMemberCountOptions = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '+3', label: '+3' },
];

const founderRepresentationOptions = [
  { key: 'woman', label: 'Woman' },
  { key: 'poc', label: 'POC (person of color)' },
  { key: 'lgbtq', label: 'LGBTQ+' },
  { key: 'immigrant', label: 'Immigrant' },
  { key: 'personWithDisabilities', label: 'Person with disabilities' },
  { key: 'seniorEntrepreneur', label: 'Senior/65+ entrepreneur' },
  { key: 'teenEntrepreneur', label: 'Teen/19- entrepreneur' },
];

type PersonEntry = {
  name: string;
  jobTitle: string;
  email: string;
  country: string;
  experience: string;
  ownership: string;
  industryExperience: string;
  technicalExperience: string;
};

const emptyPerson = (name = '', email = ''): PersonEntry => ({
  name,
  jobTitle: '',
  email,
  country: '',
  experience: '',
  ownership: '',
  industryExperience: '',
  technicalExperience: '',
});

/** Digits only, 0 through 100. */
const founderOwnershipPercent = (value: unknown): string => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100) {
    return String(value);
  }
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().replace(/%$/, '');
  if (!/^\d{1,3}$/.test(trimmed)) return '';
  const amount = Number(trimmed);
  if (amount > 100) return '';
  return String(amount);
};

const founderOwnershipError = (value: string, required: boolean): string => {
  const trimmed = value.trim();
  if (!trimmed) return required ? 'Ownership is required' : '';
  if (!/^\d{1,3}$/.test(trimmed) || Number(trimmed) > 100) {
    return 'Ownership must be a percentage from 0 to 100';
  }
  return '';
};

const founderCountFromLength = (length: number): string => {
  if (length >= 3) return '3+';
  return String(Math.max(1, length));
};

const teamCountFromLength = (length: number): string => {
  if (length >= 4) return '+3';
  return String(Math.max(0, length));
};

const normalizePerson = (item: Partial<PersonEntry> | undefined): PersonEntry => ({
  name: item?.name || '',
  jobTitle: item?.jobTitle || '',
  email: typeof item?.email === 'string' ? item.email : '',
  country: item?.country || '',
  experience: item?.experience || '',
  ownership: founderOwnershipPercent(item?.ownership),
  industryExperience: typeof item?.industryExperience === 'string' ? item.industryExperience : '',
  technicalExperience:
    typeof item?.technicalExperience === 'string' ? item.technicalExperience : '',
});

const FoundersStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { startupProfile } = useProfile();
  const { user } = useAuth();
  const accountName = String(user?.name || '').trim();
  // Auth email wins once they verify / set a password; pricing draft covers the first visit.
  const accountEmail = String(
    user?.email?.trim() || loadPricingSignupDraft('startup')?.email || ''
  ).trim();

  const storedTeamCount = String(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (startupProfile as any)?.teamMembersCount ?? '0'
  );

  const { formData, setFormData, validateForm, errors } = useFormData({
    companyFounders: String(startupProfile?.companyFounders || '1'),
    teamMembersCount: storedTeamCount || '0',
  });

  // State for founder representation toggles (initialized with default values, will be updated from profile)
  const [founderRepresentation, setFounderRepresentation] = React.useState<Record<string, boolean>>(
    {
      woman: false,
      poc: false,
      lgbtq: false,
      immigrant: false,
      personWithDisabilities: false,
      seniorEntrepreneur: false,
      teenEntrepreneur: false,
    }
  );

  // Helper function to parse founders/team data (handles both JSON string and array formats)
  const parseFoundersOrTeamData = (
    storedData: string | Array<Partial<PersonEntry>> | undefined,
    defaultCount: number
  ): PersonEntry[] => {
    if (Array.isArray(storedData)) {
      return storedData.length > 0
        ? storedData.map(normalizePerson)
        : Array.from({ length: defaultCount }, () => emptyPerson());
    }
    if (typeof storedData === 'string' && storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (Array.isArray(parsed)) {
          return parsed.length > 0
            ? parsed.map(normalizePerson)
            : Array.from({ length: defaultCount }, () => emptyPerson());
        }
      } catch {
        // If parsing fails, return default
      }
    }
    return Array.from({ length: defaultCount }, () => emptyPerson());
  };

  // Helper to parse founder representation (handles both JSON string and array formats)
  const parseFounderRepresentation = (
    storedData: string | string[] | undefined
  ): Record<string, boolean> => {
    if (Array.isArray(storedData)) {
      // Convert array to record
      const record: Record<string, boolean> = {};
      founderRepresentationOptions.forEach(opt => {
        record[opt.key] = storedData.includes(opt.key);
      });
      return record;
    }
    if (typeof storedData === 'string' && storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      } catch {
        // If parsing fails, return default
      }
    }
    return {
      woman: false,
      poc: false,
      lgbtq: false,
      immigrant: false,
      personWithDisabilities: false,
      seniorEntrepreneur: false,
      teenEntrepreneur: false,
    };
  };

  // Parse founders data from profile; prefill founder 1 name/email from pricing/account when empty.
  const parseFoundersData = () => {
    const count = Number(formData.companyFounders) || 1;
    const finalCount = count >= 3 ? 3 : count;
    const parsed = parseFoundersOrTeamData(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.foundersData,
      finalCount
    );
    if (parsed.length > 0) {
      const first = { ...parsed[0] };
      if (!first.name.trim() && accountName) first.name = accountName;
      if (!first.email.trim() && accountEmail) first.email = accountEmail;
      parsed[0] = first;
    }
    return parsed;
  };

  const [foundersData, setFoundersData] = React.useState<PersonEntry[]>(parseFoundersData());

  // Prefill founder 1 email when auth/pricing email arrives after first paint.
  React.useEffect(() => {
    if (!accountEmail) return;
    setFoundersData(prev => {
      if (!prev.length || prev[0].email.trim()) return prev;
      const next = [...prev];
      next[0] = { ...next[0], email: accountEmail };
      return next;
    });
  }, [accountEmail]);

  // Initialize founder representation from profile
  React.useEffect(() => {
    if (startupProfile) {
      const parsed = parseFounderRepresentation(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (startupProfile as any)?.founderRepresentation
      );
      setFounderRepresentation(parsed);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update founders data array when count changes
  React.useEffect(() => {
    const count = formData.companyFounders === '3+' ? 3 : Number(formData.companyFounders) || 1;
    setFoundersData(prev => {
      const newData = [...prev];
      while (newData.length < count) {
        newData.push(emptyPerson());
      }
      if (formData.companyFounders !== '3+') {
        while (newData.length > count) {
          newData.pop();
        }
      }
      return newData;
    });
  }, [formData.companyFounders]);

  // Parse team members data from profile
  const parseTeamMembersDataFunc = () => {
    const count =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.teamMembersCount || formData.teamMembersCount || '0';
    const finalCount = count === '+3' ? 4 : Number(count) || 0;
    return parseFoundersOrTeamData(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.teamMembersData,
      finalCount
    );
  };

  const [teamMembersData, setTeamMembersData] = React.useState<PersonEntry[]>(
    parseTeamMembersDataFunc()
  );

  // Update team members data array when count changes
  React.useEffect(() => {
    const count = formData.teamMembersCount === '+3' ? 4 : Number(formData.teamMembersCount) || 0;
    setTeamMembersData(prev => {
      const newData = [...prev];
      while (newData.length < count) {
        newData.push(emptyPerson());
      }
      if (formData.teamMembersCount !== '+3') {
        while (newData.length > count) {
          newData.pop();
        }
      }
      return newData;
    });
  }, [formData.teamMembersCount]);

  // Update formData with founders information for saving (as arrays of objects)
  // Using type casting because formData is typed as Record<string, string> but we need arrays
  React.useEffect(() => {
    setFormData(
      prev =>
        ({
          ...prev,
          // Only founder 1 keeps an email; other founders never store one.
          _foundersDataArray: JSON.stringify(
            foundersData.map((founder, index) => ({
              ...founder,
              email: index === 0 ? founder.email : '',
            }))
          ),
          _teamMembersDataArray: JSON.stringify(
            teamMembersData.map(member => ({
              name: member.name,
              jobTitle: member.jobTitle,
              country: member.country,
              experience: member.experience,
            }))
          ),
        }) as Record<string, string>
    );
  }, [foundersData, teamMembersData, setFormData]);

  // Update formData with founder representation (as array)
  React.useEffect(() => {
    const representationArray = Object.keys(founderRepresentation).filter(
      key => founderRepresentation[key]
    );
    setFormData(
      prev =>
        ({
          ...prev,
          _founderRepresentationArray: JSON.stringify(representationArray),
        }) as Record<string, string>
    );
  }, [founderRepresentation, setFormData]);

  // State to control when to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);
  const [founderNameErrors, setFounderNameErrors] = React.useState<Record<number, string>>({});
  const [founderTitleErrors, setFounderTitleErrors] = React.useState<Record<number, string>>({});
  const [founderCountryErrors, setFounderCountryErrors] = React.useState<Record<number, string>>(
    {}
  );
  const [founderOwnershipErrors, setFounderOwnershipErrors] = React.useState<
    Record<number, string>
  >({});
  const [teamMemberNameErrors, setTeamMemberNameErrors] = React.useState<Record<number, string>>(
    {}
  );
  const [teamMemberTitleErrors, setTeamMemberTitleErrors] = React.useState<Record<number, string>>(
    {}
  );

  const validateFoundersAndTeam = React.useCallback(() => {
    let isValid = true;
    const nameErrors: Record<number, string> = {};
    const titleErrors: Record<number, string> = {};
    const countryErrors: Record<number, string> = {};
    const ownershipErrors: Record<number, string> = {};
    const teamNameErrors: Record<number, string> = {};
    const teamTitleErrors: Record<number, string> = {};

    if (foundersData.length < 1) isValid = false;

    foundersData.forEach((founder, index) => {
      if (!founder.name.trim()) {
        nameErrors[index] =
          index === 0 ? 'At least one founder name is required' : 'Founder name is required';
        isValid = false;
      }
      if (!founder.jobTitle.trim()) {
        titleErrors[index] = 'Title is required';
        isValid = false;
      }
      if (!founder.country.trim()) {
        countryErrors[index] = 'Country is required';
        isValid = false;
      }
      const ownershipError = founderOwnershipError(founder.ownership, true);
      if (ownershipError) {
        ownershipErrors[index] = ownershipError;
        isValid = false;
      }
    });

    const teamRequired =
      formData.teamMembersCount === '+3' ? 4 : Math.max(0, Number(formData.teamMembersCount) || 0);
    for (let index = 0; index < teamRequired; index += 1) {
      const member = teamMembersData[index] || emptyPerson();
      if (!member.name.trim()) {
        teamNameErrors[index] = 'Team member name is required';
        isValid = false;
      }
      if (!member.jobTitle.trim()) {
        teamTitleErrors[index] = 'Job title is required';
        isValid = false;
      }
    }

    return {
      isValid,
      nameErrors,
      titleErrors,
      countryErrors,
      ownershipErrors,
      teamNameErrors,
      teamTitleErrors,
    };
  }, [foundersData, formData.teamMembersCount, teamMembersData]);

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (shouldValidate) {
      const requiredFields: string[] = ['companyFounders'];
      let isValid = validateForm(requiredFields);
      const checked = validateFoundersAndTeam();
      isValid = isValid && checked.isValid;

      setFounderNameErrors(checked.nameErrors);
      setFounderTitleErrors(checked.titleErrors);
      setFounderCountryErrors(checked.countryErrors);
      setFounderOwnershipErrors(checked.ownershipErrors);
      setTeamMemberNameErrors(checked.teamNameErrors);
      setTeamMemberTitleErrors(checked.teamTitleErrors);
      setShowErrors(true);

      onValidationResult?.(isValid);
      onValidationHandled?.();

      if (isValid) {
        setTimeout(() => {
          setShowErrors(false);
          setFounderNameErrors({});
          setFounderTitleErrors({});
          setFounderCountryErrors({});
          setFounderOwnershipErrors({});
          setTeamMemberNameErrors({});
          setTeamMemberTitleErrors({});
        }, 100);
      }
    }
  }, [
    shouldValidate,
    validateForm,
    onValidationHandled,
    onValidationResult,
    validateFoundersAndTeam,
  ]);

  // Drop the save notice once required founder/team fields are filled.
  React.useEffect(() => {
    if (!showErrors) return;

    const checked = validateFoundersAndTeam();
    const foundersComplete =
      Boolean(formData.companyFounders) &&
      foundersData.length > 0 &&
      Object.keys(checked.nameErrors).length === 0 &&
      Object.keys(checked.titleErrors).length === 0 &&
      Object.keys(checked.countryErrors).length === 0 &&
      Object.keys(checked.ownershipErrors).length === 0;
    const teamComplete =
      Object.keys(checked.teamNameErrors).length === 0 &&
      Object.keys(checked.teamTitleErrors).length === 0;

    if (foundersComplete && teamComplete) {
      setShowErrors(false);
      setFounderNameErrors({});
      setFounderTitleErrors({});
      setFounderCountryErrors({});
      setFounderOwnershipErrors({});
      setTeamMemberNameErrors({});
      setTeamMemberTitleErrors({});
      onValidationResult?.(true);
      return;
    }

    setFounderNameErrors(checked.nameErrors);
    setFounderTitleErrors(checked.titleErrors);
    setFounderCountryErrors(checked.countryErrors);
    setFounderOwnershipErrors(checked.ownershipErrors);
    setTeamMemberNameErrors(checked.teamNameErrors);
    setTeamMemberTitleErrors(checked.teamTitleErrors);
  }, [
    showErrors,
    foundersData,
    teamMembersData,
    formData.companyFounders,
    formData.teamMembersCount,
    onValidationResult,
    validateFoundersAndTeam,
  ]);

  // Expose form data to parent component (convert temporary fields to proper format)
  React.useEffect(() => {
    if (onCreateProfile) {
      // Parse JSON strings back to arrays/objects
      let foundersDataParsed: PersonEntry[] = [];
      let teamMembersDataParsed: PersonEntry[] = [];
      let founderRepresentationParsed: string[] = [];

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const foundersStr = (formData as any)._foundersDataArray;
        if (foundersStr) {
          foundersDataParsed = JSON.parse(foundersStr);
        }
      } catch {
        // If parsing fails, use empty array
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const teamStr = (formData as any)._teamMembersDataArray;
        if (teamStr) {
          teamMembersDataParsed = JSON.parse(teamStr);
        }
      } catch {
        // If parsing fails, use empty array
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const repStr = (formData as any)._founderRepresentationArray;
        if (repStr) {
          founderRepresentationParsed = JSON.parse(repStr);
        }
      } catch {
        // If parsing fails, use empty array
      }

      const convertedData = {
        ...formData,
        // Convert arrays from temporary fields
        foundersData: foundersDataParsed,
        teamMembersData: teamMembersDataParsed,
        founderRepresentation: founderRepresentationParsed,
      };
      // Remove temporary fields
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (convertedData as any)._foundersDataArray;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (convertedData as any)._teamMembersDataArray;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (convertedData as any)._founderRepresentationArray;
      onCreateProfile(convertedData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={foundersIcon} title="FOUNDERS & TEAM">
        <div>
          <label
            htmlFor="foundersCount"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Company Founders *
          </label>
          <PillSelector
            value={String(formData.companyFounders || '')}
            onChange={value => setFormData(prev => ({ ...prev, companyFounders: value }))}
            options={founderCountOptions}
          />
          {showErrors && errors.companyFounders && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.companyFounders}</p>
          )}
        </div>

        <div>
          <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
            Founder Names, Title, Location, Ownership, Previous startups, Industry & Technical
            experience
          </label>
          <p className="text-gray-400 text-xs sm:text-sm mb-2 text-left">
            Name, title, country, and ownership are required for each founder.
          </p>
          <div className="space-y-10">
            {foundersData.map((founder, index) => {
              // Calculate minimum founders based on PillSelector selection
              const minFounders =
                formData.companyFounders === '3+' ? 3 : Number(formData.companyFounders) || 1;
              const isLastItem = index === foundersData.length - 1;
              const canRemove = isLastItem && foundersData.length > minFounders;
              const nameError = showErrors ? founderNameErrors[index] : undefined;
              const titleError = showErrors ? founderTitleErrors[index] : undefined;
              const countryError = showErrors ? founderCountryErrors[index] : undefined;
              const ownershipError = showErrors ? founderOwnershipErrors[index] : undefined;

              return (
                <div key={index} className="space-y-3">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <FormField
                        name={`founder-name-${index}`}
                        label={index === 0 ? 'Founder (Key Contact)' : `Founder ${index + 1}`}
                        required
                        value={founder.name}
                        onChange={e => {
                          const newFounders = [...foundersData];
                          newFounders[index] = {
                            ...newFounders[index],
                            name: e.target.value,
                          };
                          setFoundersData(newFounders);
                          if (founderNameErrors[index]) {
                            setFounderNameErrors(prev => {
                              const next = { ...prev };
                              delete next[index];
                              return next;
                            });
                          }
                        }}
                        placeholder="Name *"
                        error={nameError}
                      />
                    </div>
                    <div className="flex gap-2 pb-0.5">
                      <AddButton
                        onClick={() => {
                          const next = [...foundersData, emptyPerson()];
                          setFoundersData(next);
                          const nextCount = founderCountFromLength(next.length);
                          setFormData(prev =>
                            prev.companyFounders === nextCount
                              ? prev
                              : { ...prev, companyFounders: nextCount }
                          );
                        }}
                      />
                      {canRemove && (
                        <RemoveButton
                          onClick={() => {
                            const newFounders = foundersData.filter((_, i) => i !== index);
                            setFoundersData(newFounders);
                            const nextCount = founderCountFromLength(newFounders.length);
                            setFormData(prev =>
                              prev.companyFounders === nextCount
                                ? prev
                                : { ...prev, companyFounders: nextCount }
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <FormField
                        name={`founder-title-${index}`}
                        label=""
                        required
                        value={founder.jobTitle}
                        onChange={e => {
                          const newFounders = [...foundersData];
                          newFounders[index] = {
                            ...newFounders[index],
                            jobTitle: e.target.value,
                          };
                          setFoundersData(newFounders);
                          if (founderTitleErrors[index]) {
                            setFounderTitleErrors(prev => {
                              const next = { ...prev };
                              delete next[index];
                              return next;
                            });
                          }
                        }}
                        placeholder="Title * (CEO, Lead Developer, etc.)"
                        error={titleError}
                      />
                    </div>
                    <div className="w-[6.5rem] flex-shrink-0 sm:w-28">
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          id={`founder-ownership-${index}`}
                          name={`founder-ownership-${index}`}
                          value={founder.ownership}
                          onChange={e => {
                            const digits = e.target.value.replace(/[^\d]/g, '');
                            if (digits !== '' && Number(digits) > 100) return;
                            const newFounders = [...foundersData];
                            newFounders[index] = {
                              ...newFounders[index],
                              ownership: digits === '' ? '' : String(Number(digits)),
                            };
                            setFoundersData(newFounders);
                            if (founderOwnershipErrors[index]) {
                              setFounderOwnershipErrors(prev => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }
                          }}
                          required
                          aria-label="Ownership percent"
                          className={`w-full p-2 sm:p-2.5 pr-7 text-sm border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 ${
                            ownershipError ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0–100"
                        />
                        <span
                          aria-hidden
                          className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm ${
                            founder.ownership ? 'text-gray-900' : 'text-gray-400'
                          }`}
                        >
                          %
                        </span>
                      </div>
                      {ownershipError ? (
                        <p className="text-red-500 text-xs sm:text-sm mt-1 text-right">
                          {ownershipError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {index === 0 ? (
                    <FormField
                      name={`founder-email-${index}`}
                      label=""
                      type="email"
                      value={founder.email}
                      onChange={e => {
                        const newFounders = [...foundersData];
                        newFounders[index] = {
                          ...newFounders[index],
                          email: e.target.value,
                        };
                        setFoundersData(newFounders);
                      }}
                      placeholder={accountEmail ? 'Email address (pre-filled)' : 'Email address'}
                    />
                  ) : null}
                  <div>
                    <CountrySelect
                      name={`founder-country-${index}`}
                      value={founder.country}
                      onChange={nextCountry => {
                        const newFounders = [...foundersData];
                        newFounders[index] = {
                          ...newFounders[index],
                          country: nextCountry,
                        };
                        setFoundersData(newFounders);
                        if (founderCountryErrors[index]) {
                          setFounderCountryErrors(prev => {
                            const next = { ...prev };
                            delete next[index];
                            return next;
                          });
                        }
                      }}
                      placeholder="– Select country *"
                      className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url('data:image/svg+xml,%3csvg xmlns=\\'http://www.w3.org/2000/svg\\' fill=\\'none\\' viewBox=\\'0 0 20 20\\'%3e%3cpath stroke=\\'%236b7280\\' stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'1.5\\' d=\\'m6 8 4 4 4-4\\'/%3e%3c/svg%3e')] pr-10 ${
                        countryError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px 16px',
                        paddingRight: '40px',
                      }}
                    />
                    {countryError ? (
                      <p className="text-red-500 text-xs sm:text-sm mt-1 text-right">
                        {countryError}
                      </p>
                    ) : null}
                  </div>
                  <FormField
                    name={`founder-experience-${index}`}
                    label=""
                    value={founder.experience}
                    onChange={e => {
                      const newFounders = [...foundersData];
                      newFounders[index] = {
                        ...newFounders[index],
                        experience: e.target.value,
                      };
                      setFoundersData(newFounders);
                    }}
                    placeholder="Previous startup names & job title"
                  />
                  <FormField
                    name={`founder-industry-experience-${index}`}
                    label=""
                    value={founder.industryExperience}
                    onChange={e => {
                      const newFounders = [...foundersData];
                      newFounders[index] = {
                        ...newFounders[index],
                        industryExperience: e.target.value,
                      };
                      setFoundersData(newFounders);
                    }}
                    placeholder="Industry experience"
                  />
                  <FormField
                    name={`founder-technical-experience-${index}`}
                    label=""
                    value={founder.technicalExperience}
                    onChange={e => {
                      const newFounders = [...foundersData];
                      newFounders[index] = {
                        ...newFounders[index],
                        technicalExperience: e.target.value,
                      };
                      setFoundersData(newFounders);
                    }}
                    placeholder="Technical experience"
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
            Founder representation
          </label>
          <p className="text-gray-400 italic text-xs sm:text-sm mb-4 text-left">
            This may help you be discovered by investors with a relevant investment focus. Select
            any that apply to your founder(s).
          </p>
          <div className="space-y-3">
            {founderRepresentationOptions.map(option => (
              <Toggle
                key={option.key}
                name={option.key}
                label={option.label}
                value={founderRepresentation[option.key] || false}
                onChange={value =>
                  setFounderRepresentation(prev => ({
                    ...prev,
                    [option.key]: value,
                  }))
                }
              />
            ))}
          </div>
        </div>

        <div>
          <label
            htmlFor="teamMembersCount"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Non-founder Team members
          </label>
          <PillSelector
            value={String(formData.teamMembersCount || '')}
            onChange={value => setFormData(prev => ({ ...prev, teamMembersCount: value }))}
            options={teamMemberCountOptions}
          />
        </div>

        {teamMembersData.length > 0 && (
          <div>
            <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
              Non-Founder Team members: Name, Job title, Location, Experience
            </label>
            <div className="space-y-4">
              {teamMembersData.map((member, index) => {
                // Calculate minimum team members based on PillSelector selection
                const minMembers =
                  formData.teamMembersCount === '+3' ? 4 : Number(formData.teamMembersCount) || 0;
                const isLastItem = index === teamMembersData.length - 1;
                const canRemove = isLastItem && teamMembersData.length > minMembers;
                const nameError = showErrors ? teamMemberNameErrors[index] : undefined;
                const titleError = showErrors ? teamMemberTitleErrors[index] : undefined;

                return (
                  <div key={index} className="space-y-3">
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <FormField
                          name={`team-member-name-${index}`}
                          label=""
                          required={minMembers > 0}
                          value={member.name}
                          onChange={e => {
                            const newMembers = [...teamMembersData];
                            newMembers[index] = {
                              ...newMembers[index],
                              name: e.target.value,
                            };
                            setTeamMembersData(newMembers);
                            if (teamMemberNameErrors[index]) {
                              setTeamMemberNameErrors(prev => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }
                          }}
                          placeholder={minMembers > 0 ? 'Team member name *' : 'Team member name'}
                          error={nameError}
                        />
                      </div>
                      <div className="flex gap-2">
                        <AddButton
                          onClick={() => {
                            const next = [...teamMembersData, emptyPerson()];
                            setTeamMembersData(next);
                            const nextCount = teamCountFromLength(next.length);
                            setFormData(prev =>
                              prev.teamMembersCount === nextCount
                                ? prev
                                : { ...prev, teamMembersCount: nextCount }
                            );
                          }}
                        />
                        {canRemove && (
                          <RemoveButton
                            onClick={() => {
                              const newMembers = teamMembersData.filter((_, i) => i !== index);
                              setTeamMembersData(newMembers);
                              const nextCount = teamCountFromLength(newMembers.length);
                              setFormData(prev =>
                                prev.teamMembersCount === nextCount
                                  ? prev
                                  : { ...prev, teamMembersCount: nextCount }
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <FormField
                      name={`team-member-job-title-${index}`}
                      label=""
                      required={minMembers > 0}
                      value={member.jobTitle}
                      onChange={e => {
                        const newMembers = [...teamMembersData];
                        newMembers[index] = {
                          ...newMembers[index],
                          jobTitle: e.target.value,
                        };
                        setTeamMembersData(newMembers);
                        if (teamMemberTitleErrors[index]) {
                          setTeamMemberTitleErrors(prev => {
                            const next = { ...prev };
                            delete next[index];
                            return next;
                          });
                        }
                      }}
                      placeholder={minMembers > 0 ? 'Job title *' : 'Job title'}
                      error={titleError}
                    />
                    <div>
                      <CountrySelect
                        name={`team-member-country-${index}`}
                        value={member.country}
                        onChange={nextCountry => {
                          const newMembers = [...teamMembersData];
                          newMembers[index] = {
                            ...newMembers[index],
                            country: nextCountry,
                          };
                          setTeamMembersData(newMembers);
                        }}
                        placeholder="– Select country"
                        className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url('data:image/svg+xml,%3csvg xmlns=\\'http://www.w3.org/2000/svg\\' fill=\\'none\\' viewBox=\\'0 0 20 20\\'%3e%3cpath stroke=\\'%236b7280\\' stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'1.5\\' d=\\'m6 8 4 4 4-4\\'/%3e%3c/svg%3e')] pr-10"
                        style={{
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '16px 16px',
                          paddingRight: '40px',
                        }}
                      />
                    </div>
                    <FormField
                      name={`team-member-experience-${index}`}
                      label=""
                      value={member.experience}
                      onChange={e => {
                        const newMembers = [...teamMembersData];
                        newMembers[index] = {
                          ...newMembers[index],
                          experience: e.target.value,
                        };
                        setTeamMembersData(newMembers);
                      }}
                      placeholder="Previous startup names & job title"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </FormSection>
    </BaseStep>
  );
};

export default FoundersStep;
